/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2014, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 * @module dashboard/render/VisView
 * @see VisView
 */
define([
	'jquery',
	'underscore',
	'doT',
	'../../lib/@waca/core-client/js/core-client/ui/core/View',
	'./filter/FilterIndicator',
	'./info/InfoIndicator',
	'../../widgets/livewidget/nls/StringResources',
	'../../lib/@waca/core-client/js/core-client/utils/Utils',
	'./refreshtimer/RefreshTimerIndicator',
	'text!./templates/EmptyVisualization.template',
	'./forecastIndicator/ForecastIndicator',
	'./VisTabs',
	'../../lib/@waca/core-client/js/core-client/utils/BrowserUtils',
	'../../widgets/livewidget/util/VisUtil',
	'../../lib/@waca/dashboard-common/dist/utils/MemUtil',
	'../../lib/@waca/dashboard-common/dist/lib/@ba/ba-graphics/dist/icons-js/warning--filled_24'
], function(
	$,
	_,
	dot,
	View,
	FilterIndicator,
	InfoIndicator,
	StringResources,
	Utils,
	RefreshTimerIndicator,
	EmptyVisualizationTemplate,
	ForecastIndicator,
	VisTabs,
	BrowserUtils,
	VisUtil,
	MemUtil,
	warningIcon
) {

	//Track sub-views created to help with cleanup
	const VisViewSubViews = ['filterIndicator', 'refreshTimerIndicator', 'infoIndicator'];

	/**
	 * VisView is the base class of all Visualization views
	 * such as RaveView, GridView etc.
	 */
	const VisView = View.extend({

		//The animation speed for all operations if no animation speed is set in properties.
		DEFAULT_ANIMATION_SPEED: 900,

		//The animation type (if none is set, default is currently to fadein).
		ANIMATION_TYPES: {
			NONE: 'none',
			DEFAULT: 'default',
			FADEIN: 'fadein',
			TRANSITION: 'transition',
			GROW: 'grow',
			REVEAL: 'reveal',
			BIGDATA: 'bigdata'
		},

		init: function(options) {
			VisView.inherited('init', this, arguments);
			_.extend(this, options);

			this.dashboard = this.dashboardApi;

			this.noRotate = true;
			this.isMaximizable = true;
			this.visualization = this.content.getFeature('Visualization');
			this.transactionApi = this.dashboard.getFeature('Transaction');
			this.dashboardSettings = this.dashboard.getFeature('DashboardSettings');
			this.visDefinitions = this.dashboard.getFeature('VisDefinitions');
			this.eventGroups = this.dashboard.getFeature('EventGroups');

			this._processTemplateString();

			if (!options.proxiedView && !this._viewManagesOwnQuery()) {
				this._initIndicators();
			}

			this.visEl = (this.el.firstChild) ? (this.el.firstChild) : this.el;

			//When set by an event handler (aka filter), this value controls the animation speed
			//for views that otherwise don't animate (but do support it).
			//If the definition already has animation, it will NOT override its value.
			this.overrideDefaultAnimationSpeed = -1;

			//This setting controls Non-VIPR visualizations allowing them to fade-in/fade-out during rendering.
			//VIPR visualizations use effects which are separate from this setting.
			//By default, Non-VIPR animation is off: (new for Endor) as it causes needless flashing and makes visualizaions appear slower.
			this.animationType = this.ANIMATION_TYPES.NONE;
			this.animationSpeed = this.DEFAULT_ANIMATION_SPEED;

			//The 'renderingNewData' state (maintained by the rendersequnce) is relevant in the context of the view's render() method.
			//It means that, for a particular view.render() call, new data is being rendered.
			this._renderingNewData = false;

			if (!options.proxiedView && !this._viewManagesOwnQuery()) {
				this.createView();
			}

			if (!options.proxiedView) {
				this._addPropertyHandler();
			}

			this.smartAnnotations = this.content.getFeature('SmartAnnotations.deprecated');
		},

		createTabs: function() {
			let options = {};
			// reuse the existing DIV if already exists
			if (this.ownerWidget && this.ownerWidget.$el) {
				const $tabs = this.ownerWidget.$el.find('.visTabsView');
				options.el = $tabs.length ?  $tabs[0] : null;
			}
			this._tabs = new VisTabs(options);

			// keep it hidden until render
			this._tabs.hide();
		},

		placeAt: function(element) {
			// create the visualization tabs (if supported), intentionally left out of init function [CADBLW-7546].
			this.createTabs();
			// insert view as the first element of the content container
			this.$el.prependTo(element);
			//if tab view exists, insert tabs as the first element
			if (this._tabs){
				this._tabs.placeAt(element);
			}
		},

		// to be implemented by chilren class
		doesVisPropertyMatchExpected: function () {
			return false;
		},

		// to be implemented by chilren class
		canApplyAutoBin: function () {
			return false;
		},

		// to be implemented by chilren class
		getBinningConfig: function () {
			return undefined;
		},

		/*
		 * Reads the filter indicator config spec, and instantiates a filter indicator if applicable.
		 */
		_initFilterIndicator: function() {
			var filterSpec = this.getFilterIndicatorSpec();
			if (filterSpec && (filterSpec.localFilters || filterSpec.globalFilters || filterSpec.localTopBottoms || filterSpec.drillState)) {
				this.filterIndicator = new FilterIndicator(this.visModel, this.visualization, this.transactionApi, this.getController(), filterSpec, this.logger);
				this.ownerWidget.addIcon(this.filterIndicator.$filter, 'filterIcon');
			}
		},

		_initIndicators: function() {
			const visIndicators = this.content.getFeature('VisIndicators');
			if (visIndicators && this.ownerWidget && this.ownerWidget.$el) {
				this._initRefreshIndicator();
				this._initFilterIndicator();
				this._initInfoIndicator();
				this._initForecastingIndicator();
				let indicators = visIndicators.getIndicatorList();
				_.each(indicators, (indicator) => {
					this.ownerWidget.addIcon(indicator.icon, indicator.name);
				});
			}
		},

		_initForecastingIndicator: function() {
			this.forecastIndicator = new ForecastIndicator(
				{
					visModel:this.visModel,
					ownerWidget:this.ownerWidget,
					logger: this.logger,
					supportsForecasts: true,
					ownerView: this,
					visAPI: this.ownerWidget.visAPI,
					content: this.content
				}
			);
			this.ownerWidget.addIcon(this.forecastIndicator.getIndicator(), 'forecastIcon');
		},

		_initInfoIndicator: function() {
			this.infoIndicator = new InfoIndicator({
				visModel: this.visModel,
				ownerWidget: this.ownerWidget
			});
			this.ownerWidget.addIcon(this.infoIndicator.$el, 'infoIconDiv');
		},

		_initRefreshIndicator: function() {
			this.refreshTimerIndicator = new RefreshTimerIndicator(this.ownerWidget);
			this.ownerWidget.addIcon(this.refreshTimerIndicator.$icon, 'timerIcon');
		},


		/*
		 * Indicates whether to render filter flyouts
		 */
		getFilterIndicatorSpec: function() {
			return {
				localFilters: true,
				globalFilters: true,
				localTopbottoms: true,
				drillState: true
			};
		},

		getController: function() {
			return this.content && this.content.getFeature('InteractivityController.deprecated');
		},

		getSelector: function() {
			return this.content.getFeature('DataPointSelections.deprecated');
		},

		/*
		 * Indicates whether to render topbottom flyouts. Unlike filter, topbottom only applys to the current viz there is no global
		 */
		/**
		 * Render Sequence - override this method when needed by a specific view type.
		 * @returns promise - By default, views don't have controls....return a resolved promise with an empty object.
		 */
		whenVisControlReady: function() {
			return this._noOpRenderStep('base class whenVisControlReady called!');
		},

		/**
		 * Render Sequence - override this method when needed by a specific view type.
		 * @returns promise - By default, views don't have visSpecs....return a resolved promise with an empty object.
		 */
		whenVisSpecReady: function() {
			return this._noOpRenderStep('base class whenVisSpecReady called!');
		},

		/**
		* Called before data is requested. Use this to clean up infoIndicator, etc.
		*/
		preDataReady() {
			// Reset any warnings/errors in the info indicator as we are starting fresh
			if (this.infoIndicator) {
				this.infoIndicator.clearMessagesWithIds(['visualization_notifications', 'moreDataIndicator', 'autobinSuggestion', 'data_notifications']);
			}
		},

		_hasMappedSlots() {
			return this.visualization.getSlots().getMappedSlotList().length !== 0;
		},

		/**
		 * Render Sequence - override this method when needed by a specific view type.
		 * @returns promise - By default, make a query for the data and do not index the results (ie: RaveView is an exception).
		 */
		whenDataReady: function(renderContext) {
			let result;

			// A resize event is forced when the layout base view is initialized. This may cause a request
			// to render the visualization before the visControl is created. Guard against this.
			if (this._hasMappedSlots() && this.canExecuteQuery() && !this.hasUnavailableMetadataColumns() && !this.hasMissingFilters()) {
				renderContext.isDevInstall = this.ownerWidget.getDashboardApi().isDevInstall;
				result = this._executeQueries(renderContext).then(retData => {
					this.dashboard.triggerDashboardEvent('synchronize:pageContextFilters', { synchDataFilterEntries: retData.synchDataFilterEntries });
					if (this.filterIndicator) {
						this.filterIndicator.setSynchronizeDataFilters(retData.synchDataFilterEntries);
						this.filterIndicator.update();
					}

					this._updateInfoIndicator(retData);

					return {
						data: retData,
						sameQueryData: this.isSameQueryData(renderContext, retData)
					};
				});
			} else {
				this.filterIndicator && this.filterIndicator.update();
				result = Promise.resolve({
					data: {}
				});
			}
			return result;
		},

		_updateInfoIndicator: function(queryResults) {
			let hasMoreData;
			let queryThreshold;
			let warnings;

			const queryId = _.find(queryResults.getQueryResultIdList(), id => queryResults.getResult(id).hasMoreData());
			hasMoreData = queryId !== undefined;
			queryThreshold = hasMoreData && queryResults.getResult(queryId).getRowCount();
			warnings = queryResults.getWarningList();


			if (this.infoIndicator) {
				const infoItems = [];
				// @todo: utilize the QueryResultsAPI.getWarningList
				if (hasMoreData) {
					// add data clipping
					if (queryThreshold) {
						infoItems.push({
							id: 'moreDataIndicator',
							label: StringResources.get('moreDataIndicator', { threshold: queryThreshold })
						});
					}
					// check auto-binning: 1) is it supported and 2) not already binned
					if (this.canApplyAutoBin() && !this.hasBinnedDataItems()) {
						infoItems.push({
							id: 'autobinSuggestion',
							label: StringResources.get('autobinSuggestion'),
							isSubMessage: true
						});
					}
				}
				if (warnings && warnings.length) {
					warnings.forEach((warning) => {
						infoItems.push({
							id: warning.resourceId,
							label: StringResources.get(warning.resourceId, warning.params),
							isSubMessage: !!warning.isSubMessage
						});
					});
				}
				this.infoIndicator.addInfo([{
					id: 'data_notifications',
					label: StringResources.get('dataNotifications'),
					items: infoItems
				}]);
			}
		},

		_executeQueries: function(renderContext) {
			const queryExecution = this.content.getFeature('DataQueryExecution');
			if(renderContext.extraInfo && renderContext.extraInfo.dataQueryParams) {
				queryExecution.addRequestOptions(renderContext.extraInfo.dataQueryParams);
			}
			return queryExecution.executeQueries();
		},

		/**
		 * Render Sequence - override this method when needed by a specific view type.
		 * @returns {Promise}
		 */
		whenSetDataReady: function(/* renderContext */){
			return Promise.resolve(true);
		},

		isSameQueryData: function(renderContext = {}, queryResult) {
			// TODO clean logic when QueryAPI is live.
			if (renderContext.sameQueryData === undefined) {
				const currTag = queryResult && queryResult.getCacheValidateTag && queryResult.getCacheValidateTag();
				renderContext.sameQueryData = this._previousTag && this._previousTag === currTag;
				this._previousTag = currTag;
			}
			return !!(renderContext.sameQueryData || (renderContext.extraInfo && renderContext.extraInfo.annotationRequest));
		},

		/* For Subclass to implement */
		updateInfoIndicator: function(data) {
			if (this.infoIndicator) {
				this.infoIndicator.update(data);
			}
		},

		/**
		 * The 'renderingNewData' state (maintained by the renderSequence) is relevant in the context of the view's render() method.
		 * It means that, for a particular view.render() call, new data is being rendered.
		 * (for example, typically, render following a resize would not be renderingNewData BUT,
		 * If a resize occurs quickly after a render, 2 RenderSequence render() calls could result in one view:render() call
		 * (effecively the 2 renders are "bundled into 1" from the perspective of the view so we are renderingNewData)
		 * @param {boolean} flag - The renderingNewData value is maintained by the render sequence as follows:
		 *		true: when the data task has executed a query and has new data.
		 *		false: when the render task has completed a render
		 */
		setRenderingNewData: function(flag) {
			this._renderingNewData=flag;
		},

		/**
		 * @returns the state of the renderingNewData flag.  This call makes sense in the context of a particular view.render().
		 */
		getRenderingNewData: function() {
			return this._renderingNewData;
		},

		/**
		 * @returns true if all of the required slots are mapped and this visualization can be rendered.
		 */
		isMappingComplete: function() {
			return this.visualization.getSlots().isMappingComplete();
		},

		/**
		 * Determine whether the query can be executed.
		 * Generally the query shall execute when all required mapping is complete.
		 * However some applications may require to execute the query regardless of the slot mapping state.
		 * The config should only affect the behaviour of the query and should have no impact on the actual render
		 * of the visualization. The render should continue to render only when isMappingComplete is true.
		 */
		canExecuteQuery: function() {
			return this.dashboard.getConfiguration('forceQuery') === true || this.isMappingComplete();
		},

		isRenderWithoutCompletingMapping: function() {
			const definition = this.visualization.getDefinition();
			return definition.getProperty('renderWithoutCompletingMapping') === true;
		},

		hasUnavailableMetadataColumns: function() {
			return this.visualization.hasUnavailableMetadataColumns();
		},
		/**
		 * Render Sequence - override this method when needed by a specific view type.
		 * @returns promise - By default, views don't have visSpecs....return a resolved promise with an empty object.
		 */
		whenHighlighterReady: function() {
			return this._noOpRenderStep('base class whenHighlighterReady called!');
		},

		/**
		 * Creates an object consumable by our info indicator
		 * @param {object} id id of our info, needs a matching entry in our nls entries
		 * @param {Array} items
		 * @param {String} item.id
		 * @param {String} item.caption
		 */
		_getInfoIndicatorEntries: function(id, items) {
			return [{
				id: id,
				label: StringResources.get(id),
				items: _.map(items, (item) => {
					return {
						id: item.id,
						label: item.caption
					};
				})
			}];
		},

		_setInfoIndicatorWarningsAndErrors: function(data) {
			if (this.infoIndicator && data) {
				const errorId = 'smart_errors';
				const warningId = 'smart_warnings';
				let warnings = data.getWarningList();
				let errors = data.getErrorList();
				this.infoIndicator.addInfo(this._getInfoIndicatorEntries(warningId, warnings));
				this.infoIndicator.addInfo(this._getInfoIndicatorEntries(errorId, errors));
			}
		},

		clearInfoIndicator(...msgId) {
			// Reset any warnings/errors in the info indicator as we are starting fresh
			if (this.infoIndicator) {
				//if our mapping isn't complete, just clear out the info indicator.
				if (!this.isMappingComplete()) {
					this.infoIndicator.reset();
				} else {
					this.infoIndicator.clearMessagesWithIds(msgId);
				}
			}
		},

		clearAnnotationErrorsAndWarnings() {
			this.clearInfoIndicator('smart_errors', 'smart_warnings', 'smart_exec_support_warnings', 'annotation_service_error');
		},

		/**
		 * Render Sequence - override this method when needed by a specific view type.
		 */
		onAnnotatedResultsReady: function(data) {
			this._setInfoIndicatorWarningsAndErrors(data);
		},

		onPredictSuggestionsFail() {
			if (this.infoIndicator) {
				this.infoIndicator.addInfo([{
					id: 'annotation_service_error',
					label: StringResources.get('smart_errors'),
					items: [{
						id: 'suggestions',
						label: StringResources.get('smart_annotation_insight_unavailable')
					}]
				}]);
			}
		},

		/**
		 * Render Sequence - override this method when needed by a specific view type.
		 * @returns {object} promise
		 */
		onPredictSuggestionsReady: function() {
			this.clearInfoIndicator('annotation_service_error', 'suggestions');

			// Clear the messages so that we aren't showing stale info
			this.smartAnnotations.clearInsightsIndicatorMessages();
			if (this.content) {
				this.content.getFeature('Forecast').clearForecastIndicatorMessages();
			}
		},

		_viewManagesOwnQuery: function() {
			if (this.ownerWidget.managesOwnQueries) {
				return this.ownerWidget.managesOwnQueries === true;
			}
			return false;
		},

		/**
		 * Render Sequence:
		 * Each step in the render sequence returns a promise.
		 * For any view, or any step, this method can be used to return a resolved promise with an empty object
		 */
		_noOpRenderStep: function() {
			//By default, VisViews have no underlying VisControl...return an empty, non-null object.
			return Promise.resolve({});
		},

		_addPropertyHandler: function() {
			this.ownerWidget.model.on('change:properties', this.onChangeProperties, this);
			this.ownerWidget.model.on('change:fillColor', this.onChangeProperties, this);
		},

		/**
		 * Set up the View and standard event handlers on the VisModel.
		 */
		createView: function() {
			if (!this.visModel) {
				throw 'Invalid VisModel reference';
			}

			//Subscribe to model events
			this.visModelEvents = {
				'change:annotations': this.onChangeAnnotations,
				'change:pagecontext': this.onChangePageContext, //pageContext changes should be processed as filters.
				'change:refreshTimer': this.toggleRefreshTimerIndicator
			};

			this.ownerWidget.model.on('change:searchFilters',  this.onChangeLocalFilter, this);

			this.content.on('change:visualization:localFilters', this.onChangeLocalFilter, this);

			for (var key in this.visModelEvents) {
				if (this.visModelEvents.hasOwnProperty(key)) {
					this.visModel.on(key, this.visModelEvents[key], this);
				}
			}

			if (this.filterIndicator) {
				this.filterIndicator.initEvents();
			}

			if (this.refreshTimerIndicator) {
				this.refreshTimerIndicator.initEvents();
			}

			if (this.forecastIndicator) {
				this.forecastIndicator.initEvents();
			}

			this.content.on('all', this.onAllContentChanges, this);
			this.content.on('change:property:fillColor', this.onFillColorChange, this);
			this.content.on('change:property:queryRefresh', this.onQueryRefreshChange, this);
			this.visDefinitions.on('refresh:definition', this.onRefreshVisDefinition, this);
			this.visDefinitions.on('refresh:all', this.onRefreshAllVisDefinition, this);
			this.eventGroups.on('all', this.onChangeEventGroup, this);
		},

		onChangeEventGroup: function(event) {
			if (event.info && event.info.contentIds && event.info.contentIds.indexOf(this.content.getId()) > -1) {
				this._reRender({
					refresh: {
						dataIfQueryChanged: true
					},
					extraInfo:{
						payloadData:{
							transactionToken: event.transactionToken
						}
					}
				});
			}
		},

		onRefreshAllVisDefinition: function() {
			const visDefinition = this.visualization.getDefinition();
			if (visDefinition.getState().getError() || this.content.getFeature('state').getError()) {
				this._reRender({
					refreshAll: true
				});
			}
		},

		onRefreshVisDefinition: function(event) {
			const visDefinition = this.visualization.getDefinition();
			if (visDefinition && event && event.info && visDefinition.getId() === event.info.id) {
				this._reRender({
					refreshAll: true
				});

			}
		},

		onAllContentChanges: function (e) {
			if (e && e.info && e.info.refresh) {
				const renderOptions = {
					resizing: !!e.info.resizing,
					refresh: _.extend({}, e.info.refresh),
					extraInfo:{
						payloadData:{
							transactionToken: e.transactionToken
						}
					}
				};

				if (renderOptions.refresh.all){
					// TODO: refreshAll should be changed into 'refresh.all' to be consistent with the refresh syntax
					renderOptions.refreshAll = renderOptions.refresh.all;
				}

				// Type change
				// highlighting is included because this event may have
				// been triggered from a failure handler.
				// ensure the remaining rendering sequence is covered
				// var reRenderOptions = {
				// 	refresh: {
				// 		visSpec: true,
				// 		data: true,
				// 		highlighter: true,
				// 		predictSuggestions: true
				// 	}
				// };
				// //If currently rendering icon view, ensure the proper control is loaded.
				// reRenderOptions.refresh.visControl = true;
				// this._reRender(this.appendExtraInfoToRenderOptions(reRenderOptions, event));

				// Todo: once we removed the logic for refresh properties pane upon re-render, this should be removed too
				// UndoRedo action will update the model but the old properties UI doesn't have state management, so we need to do a refresh for this scenario
				const isUndoRedo = e.context && e.context.undoRedo && e.info.featureName !== 'ConditionalFormatting';
				renderOptions.refresh.propertiesPane = this._refreshPropertiesPane(renderOptions, isUndoRedo);

				this._reRender(renderOptions);
			}
		},

		onFillColorChange: function(event) {
			const fillColor = event && event.info && event.info.value;
			this.ownerWidget.getAPI().getVisApi().visPropertySupport.setEffectiveBackgroundColor(fillColor);
		},

		onQueryRefreshChange: function(event) {
			const autoRefresh = event && event.info && event.info.value.autoRefresh;
			this.ownerWidget.getAPI().getVisApi().visRenderSupport.setQueryRefresh(autoRefresh);
		},

		_refreshPropertiesPane: function(renderOptions, isUndoRedo) {
			return !this._isLocalViz() || renderOptions.refresh.propertiesPane || !this._isNewConditionalFormattingEnabled() || !!isUndoRedo;
		},

		_isLocalViz: function() {
			const localVizType = ['KPI', 'Singleton', 'Hierarchy', 'Crosstab', 'List', 'DataPlayer'];
			const visType = this.content.getFeature('Visualization').getType();
			return _.contains(localVizType,visType);
		},

		_isNewConditionalFormattingEnabled: function() {
			const cf_kpi = !this.dashboard.getGlassCoreSvc('.FeatureChecker').checkValue('dashboard', 'condFormat', 'disabled');
			const cf_xtab = !this.dashboard.getGlassCoreSvc('.FeatureChecker').checkValue('dashboard', 'xtabcondFormat', 'disabled');
			return cf_kpi || cf_xtab;
		},

		/**
		 * If this view has a template string, make its content a child of the root "el".
		 * If the template includes "data-attach-point" attributes, augment the view class
		 * with them.
		 * eg:
		 * <root>
		 *    <div class="x" data-attach-point='sliderNode'>
		 *    <div class="y" data-attach-point='playerButtonNode'>
		 * </root>
		 *
		 * would extend the VisView with members  sliderNode=div.x,   playerButtonNode=div.y
		 */
		_processTemplateString: function() {
			if (this.templateString) {
				//Populate the view with the template if one has been defined.
				var outTemplate = this.dotTemplate(this.templateString);
				if (!outTemplate) {
					outTemplate = this.templateString;
				}
				this.$el.html(outTemplate);

				var dataAttachPoints = this.el.querySelectorAll('div[data-attach-point]');
				var oAttachPoints = {};
				_.each(dataAttachPoints, function(dataAttachPoint) {
					oAttachPoints[dataAttachPoint.getAttribute('data-attach-point')] = dataAttachPoint;
				});
				_.extend(this, oAttachPoints);
			}
		},

		/**
		 * Resize the el for this view to the specified bounds
		 * @param bounds in form left, top, width, height.
		 */
		resize: function(bounds) {
			if (bounds) {
				// capture widget height, if zero or undefined then get parent's height
				let height = bounds.height || this.$el.parent().height();
				// if we have tabs, we must compensate for the space it takes within the widget if the mapping is complete
				if (this._tabs && this._tabs.getTabsCount() > 0 &&
					this.ownerWidget.allowShowTabs && this.ownerWidget.allowShowTabs() &&
					this.isMappingComplete()) {
					height = height - this._tabs.$el.height();
				}

				var fSetBound = function(style, styleAttr, val) {
					// Setting style may be expensive, do it only when necessary.
					if (val !== undefined && val !== parseInt(style[styleAttr], 10)) {
						style[styleAttr] = val + 'px';
					}
				};
				var style = this.el.style;
				fSetBound(style, 'left', bounds.left);
				fSetBound(style, 'top', bounds.top);
				fSetBound(style, 'width', bounds.width);
				fSetBound(style, 'height', height);
			}
		},

		/**
		 * Remove this view...cleanup any event handlers set up
		 * and perform base class cleanup.
		 */
		// @override
		remove: function(finalRemove=true) {
			this.removeIconView();
			VisView.inherited('remove', this, arguments);

			// todo livewidget_cleanup -- it is weird that we pass a finalRemove
			// It seems to be used to delayed the destruction of a vipr widget
			// but we could handle this in a better way
			VisViewSubViews.forEach(view => {
				this[view] && this[view].remove();
				this[view] = null;
			});

			// external events
			for (var key in this.visModelEvents) {
				if (this.visModelEvents.hasOwnProperty(key)) {
					this.visModel.off(key, this.visModelEvents[key], this);
				}
			}

			this.ownerWidget.model.off('change:searchFilters', this.onChangeLocalFilter, this);
			this.ownerWidget.model.off('change:properties', this.onChangeProperties, this);
			this.ownerWidget.model.off('change:fillColor', this.onChangeProperties, this);

			this.content.off('change:visualization:localFilters', this.onChangeLocalFilter, this);

			if (this._tabs) {
				this._tabs.remove();
				this._tabs = null;
			}

			this.content && this.content.off('all', this.onAllContentChanges, this);

			this.content.off('change:property:fillColor', this.onFillColorChange, this);
			this.content.off('change:property:queryRefresh', this.onQueryRefreshChange, this);
			this.visDefinitions.off('refresh:definition', this.onRefreshVisDefinition, this);
			this.visDefinitions.off('refresh:all', this.onRefreshAllVisDefinition, this);
			this.eventGroups.off('all', this.onChangeEventGroup, this);

			if (this.forecastIndicator) {
				this.forecastIndicator.remove();
			}

			if (finalRemove){
				MemUtil.destroy(this);
			}
		},

		onChangePageContext: function(event) {
			return this.onChangeFilter(event).catch(e => {
				const LOG_LEVEL = (e && e.message === 'dwErrorStaleRequest') ? 'debug' : 'error';
				this.logger[LOG_LEVEL](e);
			});
		},


		_rebuildVis: function(event, options) {
			this._rebuildPending = true;
			const transactionToken = options && options.extraInfo && options.extraInfo.payloadData && options.extraInfo.payloadData.transactionToken;
			return this.transactionApi.registerTransactionHandler(transactionToken || {}, this.ownerWidget.id + '_rebuildvis', function() {
				this.ownerWidget._rebuildVis(event).then(() => {
					this._rebuildPending = false;
				});
			}.bind(this), options);
		},

		_getRenderTransaction: function(renderOptions) {
			let transaction = renderOptions && renderOptions.extraInfo && renderOptions.extraInfo.payloadData && renderOptions.extraInfo.payloadData.transactionToken;
			return (!transaction || transaction.transactionId) ? transaction : null;
		},

		_reRender: function(options) {
			if (!this._rebuildPending){
				if (this.ownerWidget.isVisible()){
					const transactionToken = this._getRenderTransaction(options);
					// Only attempt to render requests with:
					// 1. no transaction token
					// 2. valid transaction token
					if (!transactionToken || this.transactionApi.isValidTransaction(transactionToken)) {
						return this.transactionApi.registerTransactionHandler(transactionToken || {}, this.ownerWidget.id, function(opt) {
							if (this.ownerWidget) {
								const visApi = this.ownerWidget.getAPI().getVisApi();
								return visApi.getRenderSequence().reRender(this._mergeOptions(opt));
							}
							return Promise.resolve();
						}.bind(this), options);
					}
				} else {
					this.ownerWidget.setReRenderOnShow(options);
				}
			}
			return Promise.resolve();
		},

		/**
		* @description This function will take the first object in the array and merge the 'refresh' parameters from the other objects into it
		* @param {Array} of render options
		* @returns {Object} the merged options object or the first object if there are no others to merge
		*/
		_mergeOptions: function( options ){
			const mergedOptions = options[0];
			const length = options.length;
			if (length > 1){
				for (var i=1; i<length; i++){
					mergedOptions.refresh = this._mergeRefreshOptions(mergedOptions.refresh, options[i].refresh);
					if (options[i].refreshAll){
						mergedOptions.refreshAll = options[i].refreshAll;
					}
				}
			}
			if (mergedOptions.refresh && mergedOptions.refresh.data && mergedOptions.refresh.dataIfQueryChanged){
				// data refresh trumps dataIfQueryChanged
				delete mergedOptions.refresh.dataIfQueryChanged;
			}
			return mergedOptions;
		},

		_mergeRefreshOptions: function(refreshOptions, options = {}) {
			Object.keys(options).forEach((key) => {
				if (!refreshOptions.hasOwnProperty(key)) {
					refreshOptions[key] = options[key];
				} else {
					refreshOptions[key] = refreshOptions[key] || options[key];
				}
			});
			return refreshOptions;
		},

		/**
		 * Called when a filter on the VisModel changes.
		 * By default....
		 *
		 *    re-render the view when the event sender is not the same as the receiver
		 *    re-render the selections
		 */
		// HANDLER: visModel.on('change:filters')
		onChangeFilter: function(event) {
			event = event || {};
			const changeEvent = event.changeEvent;
			const transactionToken = this.transactionApi.startTransaction(changeEvent && changeEvent.data && changeEvent.data.transactionToken);
			const sender = event && event.sender;
			return  VisUtil.validateVisDefinition(this.content, this.dashboard, { visId: this.ownerWidget.model.visId })
				.then(isValid => {
					const inScope = !changeEvent || changeEvent.isInScope(this.ownerWidget.getScope());
					if (isValid && inScope) {
						let extraInfo;
						// consider re-executing the query if the filter is applied by another visualization or filter
						// data query would be ignored if the filter originates from itself due to data point selections
						// except for self originated sticky pageContext, which can potentially generate a new query
						const isOriginatorOfSelection = changeEvent && changeEvent.senderMatches(this.content.getId());
						const dataIfQueryChanged = !isOriginatorOfSelection || !!_.find(changeEvent.getItems(), item => item.getIsSticky());
						const renderOptions = {
							refresh: {
								dataIfQueryChanged: dataIfQueryChanged,
								annotation: true
							}
						};

						if (this.filterIndicator) {
							this.filterIndicator.setSynchronizeDataFilters(null);
							this.filterIndicator.update();
						}
						if (isOriginatorOfSelection) {
							if (changeEvent && !changeEvent.brushingChanged() && _.every(changeEvent.getItems(), item=>item.getValueCount() === 0)) {
								// If the filter doesn't have any value and it is not a brushing event, do not rerender.
								return;
							}
							// ensure we don't introduce unnecessary animations upon re-render with no data change
							extraInfo = {
								entryDuration: 'zero',
								payloadData: { transactionToken }
							};
						}
						this._reRender(this.appendExtraInfoToRenderOptions(renderOptions, event, extraInfo)).then(() => {
							if (isOriginatorOfSelection) {
								this.dashboard.triggerDashboardEvent('dataInVis:selected', { payloadData: this.ownerWidget, sender: this.ownerWidget.id });
								//isOriginator means this widget is the originator of the selection change (not just one that is responding to the selection change).
								this.ownerWidget.trigger('visevent:selectionchanged', { sender: this.ownerWidget.id, isSelectionOriginator: isOriginatorOfSelection });
								this.renderFocused(!sender || (sender === this.ownerWidget.id));
							}
						});
					}
				}).finally(() => {
					this.transactionApi.endTransaction(transactionToken);
				});
		},

		toggleRefreshTimerIndicator: function(event) {
			if (this.refreshTimerIndicator.$icon) {
				if (event.autoRefresh) {
					this.refreshTimerIndicator.$icon.removeClass('dataWidgetTimersNone');
				} else {
					this.refreshTimerIndicator.$icon.addClass('dataWidgetTimersNone');
				}
			}
		},

		/**
		 * Called when a filter that only affects this widget changes.
		 * Such as from the filter dialog.
		 *
		 *    re-render the selections
		 */
		onChangeLocalFilter: function(event) {
			let renderOptions = {
				refresh: {
					data: true,
					annotation: true
				}
			};

			this.ownerWidget.updateMissingFilters();

			/* vis isn't tied to data anymore*/
			if(this._isViewSlotsEmpty()){
				this._rebuildVis(event, this.appendExtraInfoToRenderOptions(renderOptions, event));
				return;
			}

			this.visModel.setPendingFilters(false); //cancel any pending filters

			var sender = event && event.sender;


			this._reRender(this.appendExtraInfoToRenderOptions(renderOptions, event));
			this.renderFocused(!sender || (sender === this.ownerWidget.id));

			if (this.filterIndicator) {
				this.filterIndicator.update();
			}
		},

		onChangePendingFilter: function() {
			this.visModel.setPendingFilters(true);
		},

		/**
		 * Slots include vis slots and local filters slots
		 */
		_isViewSlotsEmpty: function(){
			return !this.visualization.getSlots().getMappedSlotList().length && this.visModel.getLocalFilters().isEmpty();
		},

		_annotationHasChanged: function(event) {
			const prev = event && event.changeEvent && event.changeEvent.prevValue;
			const curr = event && event.changeEvent && event.changeEvent.value;

			if ((prev && prev.selectedAnnotations && prev.selectedAnnotations.length) ||
				(curr && curr.selectedAnnotations && curr.selectedAnnotations.length)) {
				return true;
			} else {
				return false;
			}
		},

		onChangeAnnotations: function(event) {
			if (this._annotationHasChanged(event)){
				let renderOptions = {
					refresh: {
						dataIfQueryChanged: true,
						annotation: true
					},
					extraInfo: {
						annotationRequest: true
					}
				};

				return this._reRender(this.appendExtraInfoToRenderOptions(renderOptions, event));
			}
			return Promise.resolve();
		},

		onVisible: function() {},

		isVisible: function() {
			return this.$el.parent().is(':visible');
		},

		/**
		 * Called when a property (like a font or a colour) changes.
		 * By default....
		 *    re-render the view
		 *    re-render the selections
		 * @param event - an event containing the single property from the property collection that changed (eg: 'showAxisTitles').
		 */
		// HANDLER: visModel.on('change:property')
		onChangeProperties: function(modelEvent) {
			const event = {
				name:'properties',
				value: modelEvent && modelEvent.value,
				sender: null,
				changeEvent: modelEvent,
				refreshData: modelEvent && modelEvent.origCollectionEvent && modelEvent.origCollectionEvent.dataRefresh
			};

			let renderOptions = {
				refresh:{}
			};

			if ((event && event.refreshData) || renderOptions.refresh.data) {
				if(_.isEmpty(renderOptions.refresh)){
					renderOptions.refresh.data = true;
				}
			}
			// re-render the visualization
			let reRenderOptions = this.appendExtraInfoToRenderOptions(renderOptions, event);
			this._reRender(reRenderOptions);

		},

		/**
		 * Called when the container is entered.
		 */
		onEnterContainer: function() {
			/* to be overridden */
		},

		/**
		 * Called when the container is exited.
		 */
		onExitContainer: function() {
			/* to be overridden */
		},

		/**
		 * update animation settings for transitions from one state to another according to the following rules:
		 * 1) use the default defined in the definition properties unless override is set to exactly 0 (to turn it off)
		 * 2) if no animation properties in the definition, use the override value if its > 0
		 * 3) if not in the def properties and not overridden by an action, animation should be off.
		 * NOTE: by default overrideDefaultAnimationSpeed is -1 which denotes it is not enabled.
		 */
		updateAnimationSettings: function(visdefProperties) {
			var visDefAnimationSpeed = (visdefProperties && visdefProperties.animationSpeed) ? visdefProperties.animationSpeed : 0;
			this.animationSpeed = (visDefAnimationSpeed) ? visDefAnimationSpeed : this.DEFAULT_ANIMATION_SPEED;
			if (this.overrideDefaultAnimationSpeed === 0 || this.visModel.getSuppressViewAnimations()) {
				//If an action has explicitly turned animation off, respect that option.
				this.animationSpeed = 0;
			} else if (this.overrideDefaultAnimationSpeed > 0 && (visDefAnimationSpeed === 0)) {
				//If animation is not on, use the override if its not off (-1)
				this.animationSpeed = this.overrideDefaultAnimationSpeed;
			}
		},

		/**
		 * VIPR supports animation effects (which are always enabled)
		 * It is possible to do a fade-in/fade-out type animation for non-VIPR visualizations.
		 * In this case, renderComplete should be called by animate not render.
		 * NOTE: This style of animation has been DISABLED for Endor and is likely to be removed/reworked in future
		 *		 as it simply makes non-VIPR visualizations flash needlessly (and appear slower).
		 * @returns true if non-VIPR animation has been enabled for a particular visualization type
		 */
		isAnimationEnabledForNonVIPRVisualizations: function() {
			return (this.animationType && this.animationType !== this.ANIMATION_TYPES.NONE);
		},

		/**
		 * For now, for grid/summary/trend etc, there is only one animation effect by default which is to fade out and fade in
		 */
		animate: function(renderInfo) {
			this.visModel.renderCompleteBeforeAnimation();
			if (this.isAnimationEnabledForNonVIPRVisualizations()) {
				const renderComplete = () => {
					this.visModel.renderComplete(renderInfo);
				};
				this.updateAnimationSettings();
				$(this.el).fadeOut(0);
				//If doing fade-in/fade-out animation, we need to call render complete at the end.
				$(this.el).fadeIn(this.animationSpeed, renderComplete);
			}
		},

		/**
		 * Override this method to take the selection and highlight it.
		 */
		renderSelected: function() {},

		/**
		 * Override this method to style focused item.
		 */
		renderFocused: function() {},

		/**
		 * The base render method is called at the end of the subtype render() to provide any actions required
		 * once render is prepared and submitted to the rendering engine (eg: RAVE).
		 *
		 * Its important to note that due to the async nature of rendering, this method may be called prior
		 * to the actual render being completed.
		 * @param {Object} renderInfo - renderInfo passed to all render methods (originates in the render sequence).
		 * @param {boolean default=true} callRenderComplete - true if this function is being called at the end of the type-specific view render
		 *			so that renderComplete will be called.  VIPRView is an exception.
		 *			VIPRView calls VisView:render to do common processing but needs to handle renderComplete itself because of the way it sets/decorates data.
		 */
		render: function(renderInfo, callRenderComplete = true) {
			this.animate(renderInfo); //Note: animate is overridden to do nothing for RAVE as it has effects support

			if (callRenderComplete && !this.isAnimationEnabledForNonVIPRVisualizations()) {
				//If animation is enabled, we need to wait until the end of the animation to call render complete.
				//See (VisView:animate()). NOTE: This style of animation is disabled for Endor.
				this.visModel.renderComplete(renderInfo);
			}
			return Promise.resolve(this);
		},

		/**
		 * Removes the icon view element and remove hide class from the rave output area whose role is application
		 */
		removeIconView: function() {
			if (!(this.hasUnavailableMetadataColumns() || this.hasMissingFilters())) {
				this._removeMissingDataColumnWarning();
			}

			if (this.$iconView) {
				this.$el.find('div[role="application"]').removeClass('hide');
				this.$el.parent().removeClass('showIconView');
				this.$iconView.remove();
				this.$iconView = null;
			}
			this._removeVisualizationDropZones();
		},

		_removeVisualizationDropZones: function() {
			const dropZonesOverlayState = this.content.getFeature('DropZonesOverlayState');
			if (dropZonesOverlayState) {
				dropZonesOverlayState.hide();
			}
		},

		/**
		 * Render warning container for this widget visualization
		 */
		_renderWarningContainer: function($containerNode) {
			this._removeWarningContainer();
			this.$warningContainer = $('<div/>', {
				'class': 'warningContainer'
			});
			$containerNode.append(this.$warningContainer);
		},

		/**
		 * Remove warning container
		 */
		_removeWarningContainer: function() {
			if (this.$warningContainer) {
				this.$warningContainer.remove();
				this.$warningContainer = null;
			}
		},

		_renderMissingDataColumnWarning: function($containerNode) {
			this._removeMissingDataColumnWarning();
			if (this.hasMissingFilters() && this.filterIndicator && this.filterIndicator.$filter) {
				this.filterIndicator.$filter.addClass('dataWidgetFiltersNone');
			}
			this.$dataUnavailable = $('<div/>', {
				'class': 'data-unavailable'
			});
			this._renderWarningContainer($containerNode);
			this.$warningContainer.append(this.$dataUnavailable);

			var $warningIcon = $('<div/>', {
				'aria-label': StringResources.get('warning'),
				'class': 'warningIcon'
			});
			this.$dataUnavailable.append($warningIcon);
			Utils.setIcon($warningIcon, warningIcon.default.id, StringResources.get('warning'));

			var $warningText = $('<div/>', {
				'class': 'warningText',
				'text': StringResources.get('datasetItemsUnavailable')
			});
			this.$dataUnavailable.append($warningText);
		},

		_removeMissingDataColumnWarning: function() {
			if (this.$dataUnavailable) {
				this.$dataUnavailable.remove();
				this.$dataUnavailable = null;
			}
			this._removeWarningContainer();
		},

		/**
		 * Shows icon of the chart type and hide the rave output area whose role is application
		 */
		renderIconView: function() {
			if (this._tabs) {
				this._tabs.hide();
			}
			var $containerNode = this._prepareRenderIconView();
			this._renderIconView($containerNode);
			this._renderVisualizationDropZones();
		},

		_renderVisualizationDropZones: function() {
			const dropZonesOverlayDOM = this.content.getFeature('DropZonesOverlayDOM');
			const dropZonesOverlayState = this.content.getFeature('DropZonesOverlayState');
			if (dropZonesOverlayState && !this.hasMissingFilters()) {
				if(dropZonesOverlayDOM.isMounted()){
					dropZonesOverlayState.show();
				} else {
					dropZonesOverlayDOM.render();
				}
			}
		},

		/**
		 * Updates the smart annotations indicator if one exists
		 */
		updateSmartsIndicators: function() {
			const suggestions = this.content.getFeature('SmartAnnotationProvider.suggestions');
			return suggestions.whenReady().then(() => {
				const smartAnnotationsIndicator = this.content.getFeature('SmartsIndicator');
				if (smartAnnotationsIndicator) {
					smartAnnotationsIndicator.update();
				}
				if (this.forecastIndicator) {
					this.forecastIndicator.update();
				}
			});
		},

		updateFPDIndicator: function() {
			// FPD shares the smae smarts indicator
			let smartAnnotationsIndicator = this.content.getFeature('SmartsIndicator');
			if(smartAnnotationsIndicator) {
				smartAnnotationsIndicator.update();
			}
		},

		/*Subclass can override the function when necessary*/
		_renderIconView: function($containerNode) {
			$containerNode.append(this.$iconView);

			this.$el.find('div[role="application"]').addClass('hide');
			this.$el.parent().addClass('showIconView');

			this.visModel.renderCompleteBeforeAnimation();
			this.visModel.renderComplete();
		},

		/**
		 * Returns the content node. Can be overriden by sub classes.
		 */
		getContentNode: function() {
			return this.contentNode ? $(this.contentNode) : this.$el;
		},

		_prepareRenderIconView: function() {
			var definition = this.visualization.getDefinition();
			var icon = definition.getPlaceholderIconUri();
			var caption = definition.getLabel();
			var $containerNode = this.getContentNode();
			if (this.hasUnavailableMetadataColumns() || this.hasMissingFilters()) {
				this._renderMissingDataColumnWarning($containerNode);
			} else if (this.$dataUnavailable) {
				this._removeMissingDataColumnWarning();
			}

			var template = dot.template(EmptyVisualizationTemplate);

			if (this.$iconView) {
				this.$iconView.remove();
			}
			this.$iconView = $(template({
				icon: icon,
				caption: caption,
				title: StringResources.get('LIVE_empty_visualization_hint_title'),
				description: StringResources.get('LIVE_empty_visualization_hint_description')
			}));
			return $containerNode;
		},

		hasMissingFilters: function() {
			return this.ownerWidget.getUnavailableLocalFilter().length > 0;
		},

		/**
		 * Uses the widget size in a given render context
		 */
		resizeToWidget: function(renderInfo) {
			this.resize(renderInfo.widgetSize);
		},

		getLabel: function() {
			var label;
			const definition = this.visualization.getDefinition();
			if (definition) {
				label = definition.getLabel();
			} else {
				label = StringResources.get('visualizationLabel');
			}
			return label;
		},

		getDescription: function() {
			var label = this.getLabel();
			var columns = this._getColumnInformationForLabel();
			var description = StringResources.get('dataWidgetDescription', {
				widgetLabel: label,
				columnNames: columns
			});
			return description;
		},

		_getColumnInformationForLabel: function() {
			var columns = '';
			const mappingInfo = this.visualization.getSlots().getMappingInfoList();
			for (var i = 0; i < mappingInfo.length; i++) {
				if (i > 0) {
					columns += ', ';
				}
				columns += mappingInfo[i].dataItem.getLabel();
			}
			return columns;
		},

		getCurrentViewSelector: function() {
			return null;
		},

		getProperties: function() {
			if (this.visModel.getDefinition().properties) {
				return Promise.resolve(this.visModel.getDefinition().properties.slice(0));
			}

			return Promise.resolve([]);
		},

		/**
		 * @returns true if any any dataItems is binned
		 */
		hasBinnedDataItems: function() {
			return !!this.visualization.getSlots().getMappingInfoList().find(mappingInfo => !!mappingInfo.dataItem.getBinning());
		},

		/**
		 * Generate thumbnail
		 *
		 * @return {Promise}
		 */
		generateThumbnail: function() {
			let isDisabled = false;
			let isMappingIncomplete = false;

			if(this._isThumbnailDisabled()){
				isDisabled = true;
			}

			if (!this.isMappingComplete()) {
				isMappingIncomplete = true;
			}

			let promise;
			if (isDisabled || isMappingIncomplete) {
				promise = Promise.resolve();
			} else {
				promise = this.dashboard.getDashboardSvc('.Thumbnail')
					.then(thumbnailSvc => thumbnailSvc.generateMarkup(this.$el.get(0)));
			}
			return promise
				.then(thumbnail => ({
					isMappingIncomplete,
					isDisabled,
					thumbnail
				}));
		},

		_isThumbnailDisabled: function() {
			//TODO: Augment this to disable thumbnail for any declared unsupportedBrowsers
			// disable thumbnails only for IE11 and lower; Edge is supposedly behaving good in most cases
			if(BrowserUtils.isIE() && !BrowserUtils.isIEEdge()){
				const defn = this.visModel.getDefinition();
				if(defn && defn.thumbnailConfig){
					//Disable thumbnails for visualizaions that delcares not supporting certain browsers
					const { unsupportedBrowsers = [] } = defn.thumbnailConfig;
					return unsupportedBrowsers.indexOf('IE') !== -1;
				}
			}
			return false;
		},

		onSummaryDataCellError: function() {
			let errorMsg = StringResources.get('errorCellWarning');
			this.infoIndicator.addInfo([{
				id: 'error_summary_cell',
				title: errorMsg,
				items: [{
					id: 'error_summary_cell',
					label: errorMsg,
					isSubMessage: true
				}]
			}]);
		},

		appendExtraInfoToRenderOptions: function(renderOptions, event, extraInfo){
			event = (event && event.changeEvent) || event;
			if (event && event.data){
				if (renderOptions.extraInfo){
					renderOptions.extraInfo.payloadData = event.data;
				} else {
					renderOptions.extraInfo = {
						payloadData: event.data
					};
				}
			}

			if (renderOptions && renderOptions.extraInfo && extraInfo) {
				renderOptions.extraInfo = _.extend(renderOptions.extraInfo, extraInfo);
			}

			if(event && event.transactionToken) {
				const { extraInfo = {} } = renderOptions;
				if(_.isEmpty(extraInfo)) {
					renderOptions.extraInfo = extraInfo;
				}
				const { payloadData = {} } = extraInfo;
				if(_.isEmpty(payloadData)) {
					renderOptions.extraInfo.payloadData = payloadData;
				}
				payloadData.transactionToken = event.transactionToken;
			}

			return renderOptions;
		}
	});

	return VisView;
});
